import { CategoryKey } from '@seatsio/seatsio-react';
import { isAfter } from 'date-fns';

import {
  type Cart,
  Categories,
  hasSeatedItems,
  LocalCartItem,
} from 'restful/tickets';

import { SeatingCategory, SeatingToken } from 'components/@seating/types';

/** Dato ticket status is blocking purchase unless a schedule date has already passed. */
export const isTicketStatusActive = (
  status?: {
    statusType?: string;
    schedule?: string | null;
  } | null,
) => {
  if (!status) {
    return false;
  }

  if (status.statusType === 'schedule' && status.schedule) {
    if (isAfter(new Date(), new Date(status.schedule))) {
      return false;
    }
  }

  return true;
};

/** Dato types statusType as string. The kit chip only accepts these two. */
export const toTicketStatusType = (
  statusType?: string,
): 'custom' | 'schedule' | undefined => {
  if (statusType === 'custom' || statusType === 'schedule') {
    return statusType;
  }

  return undefined;
};

export const mapMatchingSeatedTickets = (
  categories: Categories,
  chartKey: string,
): Array<SeatingCategory & { timeslots: Array<string> }> =>
  categories.reduce<Array<SeatingCategory & { timeslots: Array<string> }>>(
    (acc, category) => {
      category.tickets.forEach((t) => {
        if (t.seatingData?.seatingChartEventOrSeasonId === chartKey) {
          acc.push({
            ticketId: t.id,
            name: t.name,
            categoryKey: t.seatingData.seatingCategoryKey,
            timeslots: t.timeslots?.map((ts) => ts.dateTimeRange.start) ?? [],
          });
        }
      });
      return acc;
    },
    [],
  );

/**
 * Check if cart has any discount applied by checking cart modifiers
 * @param modifiers
 */
export const cartHasDiscount = (modifiers: Cart['modifiers'] | null) => {
  return modifiers?.find((item) => {
    return item?.type === 'CartTotalDiscount' && item?.value > 0;
  });
};

/**
 * Ticketing API exposes categoryKeys as strings, but Seats.io expects string or number.
 * Use this function when interacting with Seats.io to ensure compatibility.
 */
export const castStringToCategoryKey = (categoryKey: string): CategoryKey => {
  const num = Number(categoryKey);
  return isNaN(num) ? categoryKey : num;
};

export const getHoldToken = (
  seatingToken: SeatingToken | undefined,
  cartItems: Array<LocalCartItem>,
) => {
  // Only pass a holdToken if we have it not expired and we have seated items
  if (!seatingToken || seatingToken.expired || !hasSeatedItems(cartItems)) {
    return undefined;
  }

  return seatingToken.token;
};
